import type { CurvaItem } from "@/modules/financeiro-configuracoes";
import { CATEGORIA_ROTULOS, PREVIA_DURANTE_MES } from "./curvas.constants";
import type { PreviaFase } from "./types-curvas";

const PERCENTUAL_FORMATTER = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 });
const PERCENTUAL_MES_FORMATTER = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const ALTERADA_EM_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export const formatPercentual = (valor: number): string => `${PERCENTUAL_FORMATTER.format(valor)}%`;

export const formatMeses = (meses: number): string => `${meses} ${meses === 1 ? "mês" : "meses"}`;

export const formatFase = (percentual: number, meses: number): string =>
  `${formatPercentual(percentual)} · ${formatMeses(meses)}`;

export function formatAlteradaEm(instante: string): string {
  const data = new Date(instante);
  return Number.isNaN(data.getTime())
    ? instante
    : ALTERADA_EM_FORMATTER.format(data).replace(",", "");
}

export const rotuloCategoria = (categoria: CurvaItem["categoria"]): string =>
  CATEGORIA_ROTULOS[categoria];

export function dividirPercentual(percentual: number, meses: number): number[] {
  if (meses <= 0) return [];
  const centesimos = Math.round(percentual * 100);
  const base = Math.floor(centesimos / meses);
  return Array.from(
    { length: meses },
    (_, indice) => (indice === 0 ? centesimos - base * (meses - 1) : base) / 100,
  );
}

export function montarPrevia({
  antes,
  durante,
  depois,
}: Pick<CurvaItem, "antes" | "durante" | "depois">): PreviaFase[] {
  const mesesAntes = dividirPercentual(antes.percentual, antes.meses);
  const mesesApos = dividirPercentual(depois.percentual, depois.meses);

  const fases: PreviaFase[] = [];
  if (mesesAntes.length > 0) {
    fases.push({
      rotulo: "Antes",
      colunas: mesesAntes.map((percentual, indice) => ({
        mes: `M-${mesesAntes.length - indice}`,
        percentual: `${PERCENTUAL_MES_FORMATTER.format(percentual)}%`,
      })),
    });
  }
  fases.push({
    rotulo: "Durante",
    colunas: [
      {
        mes: PREVIA_DURANTE_MES,
        percentual: `${formatPercentual(durante.percentual)} ÷ meses da Obra`,
      },
    ],
  });
  if (mesesApos.length > 0) {
    fases.push({
      rotulo: "Após",
      colunas: mesesApos.map((percentual, indice) => ({
        mes: `Fim + ${indice + 1}`,
        percentual: `${PERCENTUAL_MES_FORMATTER.format(percentual)}%`,
      })),
    });
  }
  return fases;
}

const faseComMeses = (rotulo: string, percentual: number, meses: number): string =>
  `${rotulo}: ${formatPercentual(percentual)}${meses > 0 ? ` em ${formatMeses(meses)}` : ""}`;

export const formatResumo = ({
  antes,
  durante,
  depois,
}: Pick<CurvaItem, "antes" | "durante" | "depois">): string =>
  [
    faseComMeses("Antes", antes.percentual, antes.meses),
    `Durante: ${formatPercentual(durante.percentual)} no prazo da Obra`,
    faseComMeses("Após", depois.percentual, depois.meses),
  ].join(" · ");
